import React, { useState, useRef, useEffect } from 'react';
// Duplicate import removed
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, 
  FaHome, 
  FaMapMarkerAlt, 
  FaRupeeSign,
  FaBed,
  FaBath,
  FaUsers,
  FaImage,
  FaPlus,
  FaCamera,
  FaUpload,
  FaTrash,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiUrl, API_BASE_URL } from '../config/api';
import OfferStatusBadge from './OfferStatusBadge';
import { validateOfferDateRange, computeOfferStatus } from '../utils/offerStatus';
import './AddPropertyModal.css';

const EditPropertyModal = ({ isOpen, onClose, property, onPropertyUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hotel',
    description: '',
    address: '',
    city: '',
    pricePerNight: '',
    bedrooms: 1,
    bathrooms: 1,
    maxGuests: 2,
    amenities: [],
    houseRules: [],
    images: [],
    existingImages: [],
    customAmenities: [],
    customHouseRules: [],
    nearbyPlaces: [],
    touristPlaces: [],
    cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
    paymentPolicy: 'Pay at property',
    additionalNotes: '',
    // Discount/Offer fields
    discountIsActive: false,
    discountType: 'percentage',
    discountValue: '',
    discountTitle: '',
    discountDescription: '',
    discountValidFrom: '',
    discountValidUntil: '',
    discountMinStayNights: 1,
    discountMaxDiscountAmount: '',
    discountBadge: 'Special Offer',
    discountTermsAndConditions: '',
    cleaningFee: 0,
    serviceFeePercent: 10,
  });

  const [showAddAmenity, setShowAddAmenity] = useState(false);
  const [newAmenity, setNewAmenity] = useState('');
  const [showAddHouseRule, setShowAddHouseRule] = useState(false);
  const [newHouseRule, setNewHouseRule] = useState('');
  
  // Nearby Places state
  const [showAddNearbyPlace, setShowAddNearbyPlace] = useState(false);
  const [newNearbyPlace, setNewNearbyPlace] = useState({
    name: '',
    category: '',
    distance: '',
    unit: 'km'
  });
  
  // Tourist Places state
  const [showAddTouristPlace, setShowAddTouristPlace] = useState(false);
  const [newTouristPlace, setNewTouristPlace] = useState({
    name: '',
    distance: '',
    description: '',
    image: null
  });
  
  // Collapsible sections state
  const [showAmenitiesSection, setShowAmenitiesSection] = useState(false);
  const [showHouseRulesSection, setShowHouseRulesSection] = useState(false);

  const propertyTypes = ['Hotel', 'Guest House', 'PG (Paying Guest)', 'Villa', 'Apartment'];
  
  // Comprehensive amenities list
  const allAmenitiesList = [
    'WiFi', 'Air Conditioning', 'Heating', 'TV', 'Kitchen', 'Washing Machine', 'Dryer', 'Iron',
    'Hot Water', 'Toiletries', 'Hair Dryer', 'Towels', 'Bathtub',
    'Bed Linens', 'Extra Pillows', 'Hangers', 'Wardrobe', 'Sofa', 'Dining Table', 'Work Desk',
    'Refrigerator', 'Microwave', 'Oven', 'Stove', 'Dishwasher', 'Coffee Maker', 'Kettle', 'Utensils', 'Dishes & Silverware',
    'Balcony', 'Terrace', 'Garden', 'Patio', 'BBQ Grill', 'Outdoor Furniture',
    'Free Parking', 'Paid Parking', 'Street Parking', 'Lift/Elevator', 'Wheelchair Accessible',
    'CCTV', 'Security Guard', 'Fire Extinguisher', 'Smoke Detector', 'First Aid Kit', 'Safe/Locker',
    'Cable TV', 'Netflix', 'Amazon Prime', 'Gaming Console', 'Books', 'Board Games',
    'Room Service', 'Housekeeping', 'Laundry Service', 'Breakfast Included', 'Restaurant', 'Gym', 'Swimming Pool', 'Spa', 'Concierge',
    'Crib', 'High Chair', 'Baby Bath', 'Children\'s Books', 'Kids Toys',
    'Pets Allowed', 'Pet Bowls', 'Pet Bed',
    'Dedicated Workspace', 'Fast WiFi (100+ Mbps)', 'Printer', 'Meeting Room',
    'Power Backup', 'Water Purifier', 'Geyser', 'Fan', 'Mosquito Net', 'Intercom', 'Doorman'
  ];

  // Comprehensive house rules list
  const allHouseRulesList = [
    'Check-in: 2 PM - 10 PM', 'Check-out: 11 AM', 'Quiet Hours: 10 PM - 8 AM',
    'No Smoking', 'No Pets', 'No Parties/Events', 'No Unregistered Guests', 'Maximum Guests Limit',
    'Respect Neighbors', 'Keep Property Clean', 'No Loud Music', 'No Illegal Activities',
    'ID Proof Required', 'No Candles/Incense', 'No Cooking in Bedroom', 'Emergency Exit Awareness',
    'Damage Charges Apply', 'Security Deposit Required', 'Report Damages Immediately',
    'No Commercial Use', 'No Long-term Storage', 'Conserve Water & Electricity',
    'Shoes Off Inside', 'No Outside Food in Pool Area', 'Parking in Designated Area Only', 'Dispose Garbage Properly'
  ];

  // Load property data when modal opens
  useEffect(() => {
    if (property && isOpen) {
      const discount = property.discount || {};
      setFormData({
        name: property.name || '',
        type: property.type || 'Hotel',
        description: property.description || '',
        address: property.address || '',
        city: property.city || '',
        pricePerNight: property.pricePerNight || '',
        bedrooms: property.bedrooms || 1,
        bathrooms: property.bathrooms || 1,
        maxGuests: property.maxGuests || 2,
        amenities: property.amenities || [],
        houseRules: property.houseRules || [],
        images: [],
        existingImages: property.images || [],
        customAmenities: [],
        customHouseRules: [],
        nearbyPlaces: property.nearbyPlaces || [],
        touristPlaces: property.touristPlaces || [],
        cancellationPolicy: property.policies?.cancellationPolicy || 'Free cancellation up to 24 hours before check-in',
        paymentPolicy: property.policies?.paymentPolicy || 'Pay at property',
        additionalNotes: property.policies?.additionalNotes || '',
        // Discount fields
        discountIsActive: discount.isActive || false,
        discountType: discount.type || 'percentage',
        discountValue: discount.value || '',
        discountTitle: discount.title || '',
        discountDescription: discount.description || '',
        discountValidFrom: discount.validFrom ? new Date(discount.validFrom).toISOString().split('T')[0] : '',
        discountValidUntil: discount.validUntil ? new Date(discount.validUntil).toISOString().split('T')[0] : '',
        discountMinStayNights: discount.minStayNights || 1,
        discountMaxDiscountAmount: discount.maxDiscountAmount || '',
        discountBadge: discount.badge || 'Special Offer',
        discountTermsAndConditions: discount.termsAndConditions || '',
        cleaningFee: property.cleaningFee ?? 0,
        serviceFeePercent: property.serviceFeePercent ?? 10,
      });
    }
  }, [property, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const sanitizeIntegerInput = (rawValue) => String(rawValue ?? '').replace(/\D/g, '');

  const handleIntegerInputChange = (field, rawValue, { max } = {}) => {
    const digits = sanitizeIntegerInput(rawValue);
    if (digits === '') {
      handleInputChange(field, '');
      return;
    }
    let parsed = parseInt(digits, 10);
    if (!Number.isFinite(parsed)) return;
    if (max != null && parsed > max) parsed = max;
    handleInputChange(field, parsed);
  };

  const handleIntegerInputBlur = (field, { min = 1, fallback = min } = {}) => {
    const current = formData[field];
    if (current === '' || current === null || current === undefined) {
      handleInputChange(field, fallback);
      return;
    }
    const parsed = parseInt(current, 10);
    handleInputChange(field, Number.isFinite(parsed) ? Math.max(min, parsed) : fallback);
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleAddCustomAmenity = () => {
    if (newAmenity && newAmenity.trim()) {
      setFormData(prev => ({
        ...prev,
        customAmenities: [...prev.customAmenities, newAmenity.trim()]
      }));
      setNewAmenity('');
      setShowAddAmenity(false);
    }
  };

  const handleRemoveCustomAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      customAmenities: prev.customAmenities.filter((_, i) => i !== index)
    }));
  };

  const handleHouseRuleToggle = (rule) => {
    setFormData(prev => ({
      ...prev,
      houseRules: prev.houseRules.includes(rule)
        ? prev.houseRules.filter(r => r !== rule)
        : [...prev.houseRules, rule]
    }));
  };

  const handleAddCustomHouseRule = () => {
    if (newHouseRule && newHouseRule.trim()) {
      setFormData(prev => ({
        ...prev,
        customHouseRules: [...prev.customHouseRules, newHouseRule.trim()]
      }));
      setNewHouseRule('');
      setShowAddHouseRule(false);
    }
  };

  const handleRemoveCustomHouseRule = (index) => {
    setFormData(prev => ({
      ...prev,
      customHouseRules: prev.customHouseRules.filter((_, i) => i !== index)
    }));
  };

  // Nearby Places handlers
  const handleAddNearbyPlace = () => {
    if (newNearbyPlace.name && newNearbyPlace.distance) {
      setFormData(prev => ({
        ...prev,
        nearbyPlaces: [...prev.nearbyPlaces, { ...newNearbyPlace, distance: parseFloat(newNearbyPlace.distance) }]
      }));
      setNewNearbyPlace({ name: '', category: '', distance: '', unit: 'km' });
      setShowAddNearbyPlace(false);
    }
  };

  const handleRemoveNearbyPlace = (index) => {
    setFormData(prev => ({
      ...prev,
      nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== index)
    }));
  };

  // Tourist Places handlers
  const handleAddTouristPlace = () => {
    if (newTouristPlace.name && newTouristPlace.distance) {
      setFormData(prev => ({
        ...prev,
        touristPlaces: [...prev.touristPlaces, { ...newTouristPlace, distance: parseFloat(newTouristPlace.distance) }]
      }));
      setNewTouristPlace({ name: '', distance: '', description: '', image: null });
      setShowAddTouristPlace(false);
    }
  };

  const handleRemoveTouristPlace = (index) => {
    setFormData(prev => ({
      ...prev,
      touristPlaces: prev.touristPlaces.filter((_, i) => i !== index)
    }));
  };

  const handleTouristPlaceImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (validTypes.includes(file.type)) {
        setNewTouristPlace(prev => ({ ...prev, image: file }));
      } else {
        toast.warning('Only JPG, PNG, and WEBP images are allowed.');
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only JPG, PNG, and WEBP images are allowed.');
    }

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles]
      }));
    }

    e.target.value = '';
  };

  const handleCameraCapture = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...files]
      }));
    }
    e.target.value = '';
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleExistingImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Property name is required');
      return;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return;
    }
    if (!formData.pricePerNight || formData.pricePerNight <= 0) {
      setError('Valid price per night is required');
      return;
    }
    if (formData.existingImages.length === 0 && formData.images.length === 0) {
      setError('At least one image is required');
      return;
    }

    setLoading(true);
    setUploadingImages(true);

    try {
      const token = localStorage.getItem('token');
      const propertyId = property._id || property.id;
      
      // Prepare FormData for multipart/form-data
      const submitFormData = new FormData();
      submitFormData.append('name', formData.name);
      submitFormData.append('type', formData.type);
      submitFormData.append('description', formData.description || '');
      submitFormData.append('address', formData.address || '');
      submitFormData.append('city', formData.city);
      submitFormData.append('pricePerNight', formData.pricePerNight);
      submitFormData.append('cleaningFee', formData.cleaningFee ?? 0);
      submitFormData.append('serviceFeePercent', formData.serviceFeePercent ?? 10);
      submitFormData.append('bedrooms', formData.bedrooms);
      submitFormData.append('bathrooms', formData.bathrooms);
      submitFormData.append('maxGuests', formData.maxGuests);
      
      // Combine standard and custom amenities
      const allAmenities = [...formData.amenities, ...formData.customAmenities];
      submitFormData.append('amenities', JSON.stringify(allAmenities));
      
      // Combine selected house rules and custom rules
      const allHouseRules = [...formData.houseRules, ...formData.customHouseRules];
      submitFormData.append('houseRules', JSON.stringify(allHouseRules));
      
      // Append existing images (URLs)
      submitFormData.append('existingImages', JSON.stringify(formData.existingImages));
      
      // Append new images
      formData.images.forEach((image) => {
        submitFormData.append('images', image);
      });

      // Append nearby places
      submitFormData.append('nearbyPlaces', JSON.stringify(formData.nearbyPlaces));

      // Append tourist places data (without images first)
      const touristPlacesData = formData.touristPlaces.map(tp => ({
        name: tp.name,
        distance: tp.distance,
        description: tp.description,
        image: tp.image ? '' : (tp.image || '') // Keep existing image URL if no new image
      }));
      submitFormData.append('touristPlaces', JSON.stringify(touristPlacesData));

      // Append tourist place images separately (only new ones)
      formData.touristPlaces.forEach((tp, index) => {
        if (tp.image && typeof tp.image !== 'string') {
          submitFormData.append('touristPlaceImages', tp.image);
        }
      });

      // Append policy data
      submitFormData.append('policies', JSON.stringify({
        cancellationPolicy: formData.cancellationPolicy,
        paymentPolicy: formData.paymentPolicy,
        additionalNotes: formData.additionalNotes
      }));

      // Append discount data
      if (formData.discountIsActive) {
        const dateCheck = validateOfferDateRange(formData.discountValidFrom, formData.discountValidUntil);
        if (!dateCheck.valid) {
          toast.error(dateCheck.message);
          setLoading(false);
          return;
        }
        const offerStatus = computeOfferStatus({
          startDate: formData.discountValidFrom,
          endDate: formData.discountValidUntil,
        });
        submitFormData.append('discount', JSON.stringify({
          isActive: true,
          status: offerStatus,
          type: formData.discountType,
          value: parseFloat(formData.discountValue) || 0,
          title: formData.discountTitle,
          description: formData.discountDescription,
          validFrom: formData.discountValidFrom ? new Date(formData.discountValidFrom) : null,
          validUntil: formData.discountValidUntil ? new Date(formData.discountValidUntil) : null,
          minStayNights: formData.discountMinStayNights,
          maxDiscountAmount: formData.discountMaxDiscountAmount ? parseFloat(formData.discountMaxDiscountAmount) : null,
          badge: formData.discountBadge,
          termsAndConditions: formData.discountTermsAndConditions
        }));
      } else {
        // Send empty discount object to disable discount
        submitFormData.append('discount', JSON.stringify({ isActive: false }));
      }

      const response = await fetch(getApiUrl(`/api/properties/${propertyId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitFormData,
      });

      const result = await response.json();

      if (result.success) {
        onPropertyUpdated(result.property);
        toast.success('Property updated successfully!');
        onClose();
      } else {
        setError(result.message || 'Failed to update property');
      }
    } catch (err) {
      console.error('Update property error:', err);
      setError('Failed to update property. Please try again.');
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={onClose}>
        <motion.div
          className="add-property-modal"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Edit Property</h2>
            <button className="close-button" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="property-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-section">
              <h3><FaHome /> Basic Information</h3>
              
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Cozy 2BHK Apartment in Bandra"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Property Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    required
                  >
                    {propertyTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="e.g., Mumbai"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Full address"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your property..."
                  rows={4}
                />
              </div>
            </div>

            <div className="form-section">
              <h3><FaBed /> Property Details</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label><FaBed /> Bedrooms</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={formData.bedrooms}
                    onChange={(e) => handleIntegerInputChange('bedrooms', e.target.value, { max: 50 })}
                    onBlur={() => handleIntegerInputBlur('bedrooms', { min: 0, fallback: 1 })}
                    placeholder="1"
                  />
                </div>

                <div className="form-group">
                  <label><FaBath /> Bathrooms</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={formData.bathrooms}
                    onChange={(e) => handleIntegerInputChange('bathrooms', e.target.value, { max: 50 })}
                    onBlur={() => handleIntegerInputBlur('bathrooms', { min: 1, fallback: 1 })}
                    placeholder="1"
                  />
                </div>

                <div className="form-group">
                  <label><FaUsers /> Max Guests</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={formData.maxGuests}
                    onChange={(e) => handleIntegerInputChange('maxGuests', e.target.value, { max: 100 })}
                    onBlur={() => handleIntegerInputBlur('maxGuests', { min: 1, fallback: 1 })}
                    placeholder="2"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3><FaRupeeSign /> Pricing</h3>
              
              <div className="form-group">
                <label>Price per Night (₹) *</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pricePerNight}
                  onChange={(e) => handleInputChange('pricePerNight', parseFloat(e.target.value) || '')}
                  placeholder="e.g., 2500"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cleaning Fee (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.cleaningFee ?? 0}
                    onChange={(e) => handleInputChange('cleaningFee', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 200"
                  />
                  <span className="field-hint">One-time fee per booking</span>
                </div>
                <div className="form-group">
                  <label>Service Fee (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={formData.serviceFeePercent ?? 10}
                    onChange={(e) => handleInputChange('serviceFeePercent', parseFloat(e.target.value) || 10)}
                    placeholder="10"
                  />
                  <span className="field-hint">% of room price</span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div 
                className="collapsible-header"
                onClick={() => setShowAmenitiesSection(!showAmenitiesSection)}
              >
                <h3>Amenities & Facilities</h3>
                {showAmenitiesSection ? <FaChevronUp /> : <FaChevronDown />}
              </div>
              
              {showAmenitiesSection && (
                <>
                  <p className="section-description">Select all amenities available at your property</p>
              
              <div className="amenities-grid">
                {allAmenitiesList.map(amenity => (
                  <label key={amenity} className="amenity-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => handleAmenityToggle(amenity)}
                    />
                    <span>{amenity}</span>
                  </label>
                ))}
                
                {formData.customAmenities.map((amenity, index) => (
                  <label key={`custom-${index}`} className="amenity-checkbox custom-amenity">
                    <input
                      type="checkbox"
                      checked
                      readOnly
                    />
                    <span>{amenity}</span>
                    <button
                      type="button"
                      className="remove-custom"
                      onClick={() => handleRemoveCustomAmenity(index)}
                      title="Remove custom amenity"
                    >
                      <FaTrash />
                    </button>
                  </label>
                ))}
              </div>
              
              {showAddAmenity ? (
                <div className="add-amenity-input-container">
                  <input
                    type="text"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    placeholder="Enter custom amenity"
                    className="add-amenity-input"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomAmenity();
                      }
                    }}
                  />
                  <div className="add-amenity-buttons">
                    <button type="button" className="btn-confirm-amenity" onClick={handleAddCustomAmenity}>
                      Add
                    </button>
                    <button type="button" className="btn-cancel-amenity" onClick={() => setShowAddAmenity(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn-add-more" onClick={() => setShowAddAmenity(true)}>
                  <FaPlus /> Add Custom Amenity
                </button>
              )}
              
              {formData.amenities.length > 0 && (
                <div className="selected-count">
                  <strong>{formData.amenities.length + formData.customAmenities.length}</strong> amenities selected
                </div>
              )}
                </>
              )}
            </div>

            <div className="form-section">
              <h3><FaImage /> Images</h3>
              <p className="section-description">Manage property images</p>
              
              <div className="images-section">
                <div className="images-grid">
                  {/* Existing Images */}
                  {formData.existingImages.map((imageUrl, index) => (
                    <div key={`existing-${index}`} className="image-preview">
                      <img src={imageUrl} alt={`Property ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => handleExistingImageRemove(index)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  
                  {/* New Images */}
                  {formData.images.map((image, index) => (
                    <div key={`new-${index}`} className="image-preview">
                      <img src={URL.createObjectURL(image)} alt={`New ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => handleImageRemove(index)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  
                  <div className="image-upload-buttons">
                    <button type="button" className="upload-button" onClick={() => fileInputRef.current?.click()}>
                      <FaUpload />
                      <span>Upload Files</span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    
                    <button type="button" className="upload-button camera-button" onClick={() => cameraInputRef.current?.click()}>
                      <FaCamera />
                      <span>Camera</span>
                    </button>
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      onChange={handleCameraCapture}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div 
                className="collapsible-header"
                onClick={() => setShowHouseRulesSection(!showHouseRulesSection)}
              >
                <h3>House Rules</h3>
                {showHouseRulesSection ? <FaChevronUp /> : <FaChevronDown />}
              </div>
              
              {showHouseRulesSection && (
                <>
                  <p className="section-description">Select all rules that apply to your property</p>
              
              <div className="house-rules-grid">
                {allHouseRulesList.map(rule => (
                  <label key={rule} className="house-rule-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.houseRules.includes(rule)}
                      onChange={() => handleHouseRuleToggle(rule)}
                    />
                    <span>{rule}</span>
                  </label>
                ))}
                
                {formData.customHouseRules.map((rule, index) => (
                  <label key={`custom-${index}`} className="house-rule-checkbox custom-rule">
                    <input type="checkbox" checked readOnly />
                    <span>{rule}</span>
                    <button
                      type="button"
                      className="remove-custom"
                      onClick={() => handleRemoveCustomHouseRule(index)}
                      title="Remove custom rule"
                    >
                      <FaTrash />
                    </button>
                  </label>
                ))}
              </div>
              
              {showAddHouseRule ? (
                <div className="add-rule-input-container">
                  <input
                    type="text"
                    value={newHouseRule}
                    onChange={(e) => setNewHouseRule(e.target.value)}
                    placeholder="Enter custom house rule"
                    className="add-rule-input"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomHouseRule();
                      }
                    }}
                  />
                  <div className="add-rule-buttons">
                    <button type="button" className="btn-confirm-rule" onClick={handleAddCustomHouseRule}>
                      Add
                    </button>
                    <button type="button" className="btn-cancel-rule" onClick={() => setShowAddHouseRule(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn-add-more" onClick={() => setShowAddHouseRule(true)}>
                  <FaPlus /> Add Custom Rule
                </button>
              )}

              {(formData.houseRules.length > 0 || formData.customHouseRules.length > 0) && (
                <div className="selected-count">
                  <strong>{formData.houseRules.length + formData.customHouseRules.length}</strong> rules selected
                </div>
              )}
                </>
              )}
            </div>

            <div className="form-section">
              <h3>Nearby Places (Optional)</h3>
              <p className="section-description">Add nearby places like bus stops, railway stations, malls, hospitals, etc.</p>
              
              {formData.nearbyPlaces.length > 0 && (
                <div className="nearby-places-list">
                  {formData.nearbyPlaces.map((place, index) => (
                    <div key={index} className="nearby-place-item">
                      <div className="nearby-place-info">
                        <strong>{place.name}</strong>
                        {place.category && <span className="place-category"> ({place.category})</span>}
                        <span className="place-distance"> - {place.distance} {place.unit}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-custom"
                        onClick={() => handleRemoveNearbyPlace(index)}
                        title="Remove place"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {showAddNearbyPlace ? (
                <div className="add-nearby-place-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Place Name *</label>
                      <input
                        type="text"
                        value={newNearbyPlace.name}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Central Bus Stop"
                        autoFocus
                      />
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        value={newNearbyPlace.category}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, category: e.target.value }))}
                      >
                        <option value="">Select category</option>
                        <option value="Bus Stop">Bus Stop</option>
                        <option value="Railway Station">Railway Station</option>
                        <option value="Metro Station">Metro Station</option>
                        <option value="Airport">Airport</option>
                        <option value="Mall">Mall</option>
                        <option value="Hospital">Hospital</option>
                        <option value="School">School</option>
                        <option value="Restaurant">Restaurant</option>
                        <option value="Tourist Place">Tourist Place</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Distance *</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newNearbyPlace.distance}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, distance: e.target.value }))}
                        placeholder="e.g., 0.5"
                      />
                    </div>
                    <div className="form-group">
                      <label>Unit</label>
                      <select
                        value={newNearbyPlace.unit}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, unit: e.target.value }))}
                      >
                        <option value="km">Kilometers</option>
                        <option value="meters">Meters</option>
                      </select>
                    </div>
                  </div>
                  <div className="add-amenity-buttons">
                    <button
                      type="button"
                      className="btn-confirm-amenity"
                      onClick={handleAddNearbyPlace}
                    >
                      Add Place
                    </button>
                    <button
                      type="button"
                      className="btn-cancel-amenity"
                      onClick={() => {
                        setShowAddNearbyPlace(false);
                        setNewNearbyPlace({ name: '', category: '', distance: '', unit: 'km' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-add-more"
                  onClick={() => setShowAddNearbyPlace(true)}
                >
                  <FaPlus />
                  Add Nearby Place
                </button>
              )}
            </div>

            <div className="form-section">
              <h3>Tourist Attractions Nearby (Optional)</h3>
              <p className="section-description">Add tourist places with images, distance, and description</p>
              
              {formData.touristPlaces.length > 0 && (
                <div className="tourist-places-grid">
                  {formData.touristPlaces.map((place, index) => (
                    <div key={index} className="tourist-place-card">
                      {(place.image && typeof place.image === 'string') ? (
                        <div className="tourist-place-image">
                          <img 
                           src={place.image.startsWith('/uploads/') ? `${API_BASE_URL}${place.image}` : place.image} 
                            alt={place.name}
                          />
                        </div>
                      ) : place.image ? (
                        <div className="tourist-place-image">
                          <img 
                            src={URL.createObjectURL(place.image)} 
                            alt={place.name}
                          />
                        </div>
                      ) : null}
                      <div className="tourist-place-info">
                        <strong>{place.name}</strong>
                        <span className="place-distance">{place.distance} km away</span>
                        {place.description && <p className="place-description">{place.description}</p>}
                      </div>
                      <button
                        type="button"
                        className="remove-tourist-place"
                        onClick={() => handleRemoveTouristPlace(index)}
                        title="Remove tourist place"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {showAddTouristPlace ? (
                <div className="add-tourist-place-form">
                  <div className="form-group">
                    <label>Tourist Place Name *</label>
                    <input
                      type="text"
                      value={newTouristPlace.name}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Gateway of India"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>Distance (km) *</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={newTouristPlace.distance}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, distance: e.target.value }))}
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={newTouristPlace.description}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the tourist place"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Upload Image</label>
                    <div className="tourist-image-upload">
                      {newTouristPlace.image ? (
                        <div className="tourist-image-preview">
                          <img 
                            src={URL.createObjectURL(newTouristPlace.image)} 
                            alt="Preview"
                          />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => setNewTouristPlace(prev => ({ ...prev, image: null }))}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <label className="tourist-image-upload-btn">
                          <FaCamera />
                          <span>Choose Image</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleTouristPlaceImageSelect}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="add-amenity-buttons">
                    <button
                      type="button"
                      className="btn-confirm-amenity"
                      onClick={handleAddTouristPlace}
                    >
                      Add Tourist Place
                    </button>
                    <button
                      type="button"
                      className="btn-cancel-amenity"
                      onClick={() => {
                        setShowAddTouristPlace(false);
                        setNewTouristPlace({ name: '', distance: '', description: '', image: null });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-add-more"
                  onClick={() => setShowAddTouristPlace(true)}
                >
                  <FaPlus />
                  Add Tourist Attraction
                </button>
              )}
            </div>

            {/* Policy Section */}
            <div className="form-section">
              <h3>Property Policies</h3>
              <p className="section-description">Set cancellation and payment policies for your property</p>
              
              <div className="form-group">
                <label htmlFor="cancellationPolicy">
                  Cancellation Policy <span className="required">*</span>
                </label>
                <select
                  id="cancellationPolicy"
                  value={formData.cancellationPolicy}
                  onChange={(e) => setFormData({ ...formData, cancellationPolicy: e.target.value })}
                  required
                >
                  <option value="Free cancellation up to 24 hours before check-in">
                    Free cancellation up to 24 hours before check-in
                  </option>
                  <option value="Free cancellation up to 48 hours before check-in">
                    Free cancellation up to 48 hours before check-in
                  </option>
                  <option value="Free cancellation up to 7 days before check-in">
                    Free cancellation up to 7 days before check-in
                  </option>
                  <option value="Non-refundable">
                    Non-refundable
                  </option>
                  <option value="Flexible - Full refund 1 day prior to arrival">
                    Flexible - Full refund 1 day prior to arrival
                  </option>
                  <option value="Moderate - Full refund 5 days prior to arrival">
                    Moderate - Full refund 5 days prior to arrival
                  </option>
                  <option value="Strict - 50% refund up to 1 week prior to arrival">
                    Strict - 50% refund up to 1 week prior to arrival
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="paymentPolicy">
                  Payment Policy <span className="required">*</span>
                </label>
                <select
                  id="paymentPolicy"
                  value={formData.paymentPolicy}
                  onChange={(e) => setFormData({ ...formData, paymentPolicy: e.target.value })}
                  required
                >
                  <option value="Pay at property">Pay at property</option>
                  <option value="Pay online (full amount)">Pay online (full amount)</option>
                  <option value="Pay 50% now, 50% at property">Pay 50% now, 50% at property</option>
                  <option value="Pay online with free cancellation">Pay online with free cancellation</option>
                  <option value="Advance payment required">Advance payment required</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="additionalNotes">
                  Additional Policy Notes (Optional)
                </label>
                <textarea
                  id="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                  placeholder="Add any additional policy information, special conditions, or important notes for guests..."
                  rows="4"
                />
              </div>
            </div>

            {/* Discount/Offer Section */}
            <div className="form-section">
              <h3>🎯 Exclusive Offers & Discounts (Optional)</h3>
              <p className="section-description">Create special offers to attract more guests and increase bookings</p>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.discountIsActive}
                    onChange={(e) => setFormData({ ...formData, discountIsActive: e.target.checked })}
                  />
                  <span>Enable Special Offer/Discount</span>
                </label>
              </div>

              {formData.discountIsActive && (
                <>
                  {formData.discountValidFrom && formData.discountValidUntil && (
                    <div style={{ marginBottom: 12 }}>
                      <OfferStatusBadge
                        status={computeOfferStatus({
                          startDate: formData.discountValidFrom,
                          endDate: formData.discountValidUntil,
                        })}
                      />
                      <small className="field-hint" style={{ display: 'block', marginTop: 6 }}>
                        Only <strong>Active</strong> offers appear to guests. Scheduled offers start automatically; expired offers are hidden.
                      </small>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountType">
                        Discount Type <span className="required">*</span>
                      </label>
                      <select
                        id="discountType"
                        value={formData.discountType}
                        onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                        required
                      >
                        <option value="percentage">Percentage Discount</option>
                        <option value="fixed">Fixed Amount Discount</option>
                        <option value="early_bird">Early Bird Special</option>
                        <option value="last_minute">Last Minute Deal</option>
                        <option value="seasonal">Seasonal Offer</option>
                        <option value="member_deal">Member Deal</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="discountValue">
                        Discount Value <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="discountValue"
                        min="0"
                        max={formData.discountType === 'percentage' ? '100' : undefined}
                        step={formData.discountType === 'percentage' ? '1' : '50'}
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                        placeholder={formData.discountType === 'percentage' ? 'e.g., 20' : 'e.g., 500'}
                        required
                      />
                      <small className="field-hint">
                        {formData.discountType === 'percentage' ? '% off original price' : '₹ off original price'}
                      </small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountTitle">
                        Offer Title <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="discountTitle"
                        value={formData.discountTitle}
                        onChange={(e) => setFormData({ ...formData, discountTitle: e.target.value })}
                        placeholder="e.g., Weekend Special, Early Bird Discount"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="discountBadge">
                        Display Badge
                      </label>
                      <input
                        type="text"
                        id="discountBadge"
                        value={formData.discountBadge}
                        onChange={(e) => setFormData({ ...formData, discountBadge: e.target.value })}
                        placeholder="e.g., Special Offer, Limited Time"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="discountDescription">
                      Offer Description
                    </label>
                    <textarea
                      id="discountDescription"
                      value={formData.discountDescription}
                      onChange={(e) => setFormData({ ...formData, discountDescription: e.target.value })}
                      placeholder="Describe your special offer to attract guests..."
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountValidFrom">
                        Start Date <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        id="discountValidFrom"
                        value={formData.discountValidFrom}
                        onChange={(e) => setFormData({ ...formData, discountValidFrom: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="discountValidUntil">
                        End Date <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        id="discountValidUntil"
                        value={formData.discountValidUntil}
                        onChange={(e) => setFormData({ ...formData, discountValidUntil: e.target.value })}
                        min={formData.discountValidFrom || undefined}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountMinStayNights">
                        Minimum Stay (Nights)
                      </label>
                      <input
                        type="number"
                        id="discountMinStayNights"
                        min="1"
                        value={formData.discountMinStayNights}
                        onChange={(e) => setFormData({ ...formData, discountMinStayNights: parseInt(e.target.value) || 1 })}
                      />
                    </div>

                    {formData.discountType === 'percentage' && (
                      <div className="form-group">
                        <label htmlFor="discountMaxDiscountAmount">
                          Max Discount Amount (₹)
                        </label>
                        <input
                          type="number"
                          id="discountMaxDiscountAmount"
                          min="0"
                          step="50"
                          value={formData.discountMaxDiscountAmount}
                          onChange={(e) => setFormData({ ...formData, discountMaxDiscountAmount: e.target.value })}
                          placeholder="e.g., 2000"
                        />
                        <small className="field-hint">
                          Maximum discount amount for percentage discounts
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="discountTermsAndConditions">
                      Terms & Conditions
                    </label>
                    <textarea
                      id="discountTermsAndConditions"
                      value={formData.discountTermsAndConditions}
                      onChange={(e) => setFormData({ ...formData, discountTermsAndConditions: e.target.value })}
                      placeholder="Add any terms and conditions for this offer..."
                      rows="3"
                    />
                  </div>

                  {/* Discount Preview */}
                  {formData.discountValue && formData.pricePerNight && (
                    <div className="discount-preview">
                      <h4>Offer Preview</h4>
                      <div className="preview-card">
                        <div className="preview-badge">{formData.discountBadge}</div>
                        <div className="preview-title">{formData.discountTitle || 'Special Offer'}</div>
                        <div className="preview-pricing">
                          <span className="original-price">₹{parseFloat(formData.pricePerNight).toLocaleString()}</span>
                          <span className="discounted-price">
                            ₹{(() => {
                              const originalPrice = parseFloat(formData.pricePerNight);
                              const discountValue = parseFloat(formData.discountValue);
                              let discountedPrice;
                              
                              if (formData.discountType === 'percentage') {
                                const discountAmount = (originalPrice * discountValue) / 100;
                                const maxDiscount = formData.discountMaxDiscountAmount ? parseFloat(formData.discountMaxDiscountAmount) : Infinity;
                                const finalDiscountAmount = Math.min(discountAmount, maxDiscount);
                                discountedPrice = originalPrice - finalDiscountAmount;
                              } else {
                                discountedPrice = Math.max(0, originalPrice - discountValue);
                              }
                              
                              return Math.round(discountedPrice).toLocaleString();
                            })()}
                          </span>
                          <span className="savings">
                            Save {formData.discountType === 'percentage' ? `${formData.discountValue}%` : `₹${formData.discountValue}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={loading || uploadingImages}>
                {loading || uploadingImages ? 'Updating...' : 'Update Property'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EditPropertyModal;
